import { inject } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { isUndefined, omit } from 'lodash';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { AnnotationCreateDeleteService } from './annotation-create-delete.service';
import { sortObjects } from './object-sorting.service';
import { ObjectService } from './object.service';

export class MultiAnnotationCreationHelper {
  createdObjects: ObjectState[] = [];
  createdShapes: ShapeState[] = [];
  private objectService = inject(ObjectService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private annotationCreateDeleteService = inject(AnnotationCreateDeleteService);
  private objectsById: Map<number, ObjectState>;
  private shapesById: Map<number, ShapeState>;

  constructor(objectsToCreate: ObjectState[], shapesToCreate: ShapeState[]) {
    this.objectsById = new Map(
      objectsToCreate.map((object) => [object.index, object]),
    );
    this.shapesById = new Map(
      shapesToCreate.map((shape) => [shape.index, shape]),
    );
  }

  create(
    parentId: number | undefined,
    objects: ObjectState[],
    shapes: ShapeState[],
  ) {
    this.taskWorkspaceStore.batch(() => {
      let objectId = parentId;
      if (isUndefined(parentId) && shapes.length) {
        objectId = this.createObject({}, undefined).index;
      }

      this.createObjects(objects, objectId);
      shapes.forEach((shape) => this.createShape(shape, objectId));
    });
  }

  private createObjects(objects: ObjectState[], parentId: number | undefined) {
    sortObjects(objects)
      // reverse order simulated creating the objects in the same order we would
      // normally create them
      .reverse()
      .forEach((object) => {
        const newObject = this.createObject(object, parentId);
        const childObjects = object.childObjectIds
          .map((id) => this.objectsById.get(id))
          .filter(isDefined);
        const childShapes = object.childShapeIds
          .map((index) => this.shapesById.get(index))
          .filter(isDefined);
        this.create(newObject.index, childObjects, childShapes);
      });
  }

  private createObject(
    object: Partial<ObjectState>,
    parentId: number | undefined,
  ) {
    object.parentId = parentId;
    const newObject = this.objectService.createObject(object);
    this.createdObjects.push(newObject);
    return newObject;
  }

  private createShape(shape: ShapeState, parentObjectId: number | undefined) {
    assert(
      isDefined(parentObjectId),
      'should not try to create shape without parent object',
    );
    shape.index = this.taskWorkspaceStore.claimNextInternalId();

    const { outputName } = shape;
    assert(outputName, 'outputName should be defined for annotation shape');

    const shapeToCreate = omit(shape, 'linkedShapeId');

    const shapeCreated = this.annotationCreateDeleteService.createShape(
      shapeToCreate,
      outputName,
      false,
      parentObjectId,
    );
    if (shapeCreated) {
      this.createdShapes.push(shapeCreated);
    }
  }
}
